/* eslint-disable @typescript-eslint/no-unused-vars */
import logger from '../log';
import autobind from '../autobind';
import { SourceType, PlayType } from '../common/constants';
import InterceptorStack from './InterceptorStack';
import {
  HttpRequest,
  HttpResponse,
  HttpMethod,
  HttpRequestBody,
  HttpResponseBody,
  HttpResponseType
} from './interfaces';
import { EventContainer } from '../common/EventEmitter';
import {
  isArrayBuffer,
  isString,
  maybeDecode
} from '../common/utils/stringify';
import makeRequest from './makeRequest';
import { makeResponseBody } from './utils/response';

const log = logger.getLogger(__filename);

type ReadyState =
  | 0 // UNSENT
  | 1 // OPENED
  | 2 // HEADERS_RECEIVED
  | 3 // LOADING
  | 4; // DONE

interface State {
  readyState: ReadyState;
  response?: HttpResponseBody;
  responseText?: string;
  status?: number;
  statusText?: string;
}

const ABORT = 'abort';
const ERROR = 'error';
// const TIMEOUT = 'timeout';
const READY_STATE_CHANGE = 'readystatechange';
const LOAD = 'load';
const LOAD_END = 'loadend';
const LOAD_START = 'loadstart';
const PROGRESS = 'progress';

interface ResponseByTypeResult {
  response: HttpResponseBody | undefined;
  text?: string;
}

function convertResponseByType(
  responseType: HttpResponseType,
  body: HttpResponseBody | undefined
): ResponseByTypeResult {
  const basicBody = makeResponseBody(body);
  const text: string | undefined =
    (isString(basicBody) && basicBody) || undefined;
  if (isString(basicBody) && responseType === 'arraybuffer') {
    const encoder = new TextEncoder();
    return { response: encoder.encode(basicBody).buffer, text };
  }
  return { response: basicBody, text };
}

// Because typescript
class RetargettedEvent implements Event {
  private event: Event;

  readonly AT_TARGET = 2;
  readonly BUBBLING_PHASE = 3;
  readonly CAPTURING_PHASE = 1;
  readonly NONE = 0;

  readonly bubbles: boolean;
  readonly cancelBubble: boolean;
  readonly cancelable: boolean;
  readonly composed: boolean;
  readonly defaultPrevented: boolean;
  readonly eventPhase: number;
  readonly isTrusted: boolean;
  readonly returnValue: boolean;
  readonly srcElement: EventTarget | null;
  readonly timeStamp: number;
  readonly type: string;
  readonly currentTarget: EventTarget | null;
  readonly target: EventTarget | null;

  constructor(event: Event, target: EventTarget) {
    this.event = event;
    this.bubbles = event.bubbles;
    this.cancelBubble = event.cancelBubble;
    this.cancelable = event.cancelable;
    this.composed = event.composed;
    this.defaultPrevented = event.defaultPrevented;
    this.eventPhase = event.eventPhase;
    this.isTrusted = event.isTrusted;
    this.returnValue = event.returnValue;
    this.srcElement = event.srcElement;
    this.timeStamp = event.timeStamp;
    this.type = event.type;
    this.currentTarget = target;
    this.target = target;
  }

  composedPath(): EventTarget[] {
    return this.event.composedPath();
  }

  initEvent(
    type: string,
    bubbles?: boolean | undefined,
    cancelable?: boolean | undefined
  ): void {
    return this.event.initEvent(type, bubbles, cancelable);
  }

  preventDefault(): void {
    this.event.preventDefault();
  }

  stopImmediatePropagation(): void {
    this.event.stopImmediatePropagation();
  }

  stopPropagation(): void {
    this.event.stopPropagation();
  }
}

const canConstructEvent: boolean = ((): boolean => {
  try {
    new Event('foo');
    return true;
  } catch (e) {
    return false;
  }
})();

// SRV-1712 - new Event cannot be called in IE 11. So these functions serve as the alternative.
function makeEvent(type: string): Event {
  if (canConstructEvent) {
    return new Event(type);
  } else {
    const evt = document.createEvent('Event');
    // IE11 requires all 3 params to be populated.
    evt.initEvent(type, false, false);
    return evt;
  }
}

// SRV-1712 - ProgressEvent cannot be created through document.createEvent. So test that it's valid before use.
const canConstructProgressEvent: boolean = ((): boolean => {
  try {
    new ProgressEvent('foo');
    return true;
  } catch (e) {
    return false;
  }
})();

function makeProgressEvent(type: string): ProgressEvent {
  if (canConstructProgressEvent) {
    return new ProgressEvent(type);
  } else {
    const evt = document.createEvent('ProgressEvent');
    // IE11 requires all 3 params to be populated.
    evt.initEvent(type, false, false);
    return evt;
  }
}

// SRV-1914: VideoJs problems with playlists.
// VideoJs sets the mimeType "text/plain; charset=x-user-defined"
// Would prefer to use TextDecoder, but the TextDecoder doesn't map the array
// of bytes directly into a string.
// VideoJs expects a literal copy of the buffer into a string without
// modification/charset.
// The TextDecoder doesn't just copy the sequence into a string for
// x-user-defined it modifies it into a UTF-16 string, and changes the bytes
// withthe high bit set. (didn't check to see all cases, but that appears to be
// what it is doing).
// Simple Example of the problem:
//   [0xbd, 0x2b, 0xbe, 0x3d] when mapped using TextDecoder('x-user-defined')
//   ends up as: 0x7fbd, 0x2b, 0xf7be, 0x3d
export const decodeXuserDefined = (buffer: ArrayBuffer): string => {
  return new Uint8Array(buffer).reduce((acc: string, val: number) => {
    return acc + String.fromCharCode(val);
  }, '');
};

const retargetEvent = (e: Event, target: KollectiveXHR): Event => {
  const event: RetargettedEvent = new RetargettedEvent(e, target);
  return event;
};

class EventEmitter implements EventTarget {
  readonly events: Map<string, EventContainer<Event>>;

  constructor() {
    this.events = new Map();
  }

  dispatchEvent(event: Event): boolean {
    const container = this.events.get(event.type);
    if (container) {
      container.emit(event);
      return true;
    }
    return false;
  }

  addEventListener(name: string, listener: EventListener): void {
    let container = this.events.get(name);
    if (!container) {
      container = new EventContainer();
      this.events.set(name, container);
    }
    container.subscribe(listener);
  }

  removeEventListener(eventName: string, listener: EventListener): void {
    const container = this.events.get(eventName);
    if (container) {
      container.unsubscribe(listener);
    }
  }

  cleanup() {
    this.events.clear();
  }
}

class InternalImpl {
  emitter: EventEmitter;
  customRequestHeaders: Map<string, string>;
  kxhr: KollectiveXHR;
  stack: InterceptorStack;
  sourceType: SourceType;
  playType: PlayType;

  url: string;
  method: HttpMethod = 'GET';
  request?: HttpRequest;
  response?: HttpResponse;

  constructor(
    kxhr: KollectiveXHR,
    stack: InterceptorStack,
    sourceType: SourceType,
    playType: PlayType
  ) {
    autobind(this);
    this.kxhr = kxhr;
    this.stack = stack;
    this.emitter = new EventEmitter();
    // cache headers added with setRequestHeader
    this.customRequestHeaders = new Map();
    this.sourceType = sourceType;
    this.playType = playType ? playType : PlayType.UNKNOWN;
    // for whatever reason empty string is acceptable as a request
    this.url = '';
  }

  open(method: HttpMethod, url: string) {
    if (this.url) {
      return;
    }
    this.kxhr.readyState = 1;
    this.method = method;
    this.url = url;
    const open = retargetEvent(makeEvent(READY_STATE_CHANGE), this.kxhr);
    if (this.kxhr.onreadystatechange) {
      this.kxhr.onreadystatechange(open);
    }
    this.emitter.dispatchEvent(open);
  }

  send(body?: HttpRequestBody): void {
    // TODO - msCaching param - msCaching is a Amp XhrWrapper property.
    const request = makeRequest(this.method, this.url, {
      body,
      headers: this.customRequestHeaders,
      responseType: this.kxhr.responseType || undefined,
      timeout: this.kxhr.timeout,
      withCredentials: this.kxhr.withCredentials
    });

    this.intercept(this.stack.handle(request));
  }

  _getContentTypeValue(url: string) {
    if (/Fragments\(video/gi.test(url)) {
      return 'video/mp4';
    }
    if (/Fragments\((audio|aac)/gi.test(url)) {
      return 'audio/mp4';
    }
    if (/\.ts/gi.test(url)) {
      return 'video/MP2T';
    }
    return null;
  }

  getResponseHeader(headerName: string): string | null {
    if (this.response && this.response.headers) {
      return this.response.headers.get(headerName.toLowerCase()) || null;
    }
    const isContentType = /^content-type$/gi.test(headerName);
    if (!isContentType && this.url) {
      // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/getResponseHeader
      // returns null when header does not exist
      return this._getContentTypeValue(this.url);
    }
    // same as !isContentType
    return null;
  }

  /**
   * see https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/getAllResponseHeaders
   */
  getAllResponseHeaders(): string {
    if (this.response && this.response.headers) {
      let out = '';
      this.response.headers.forEach(([key, value]) => {
        out = out + `${key}: ${value}\r\n`;
      });
      return out;
    }
    if (this.url) {
      const contentTypeValue = this._getContentTypeValue(this.url);
      if (contentTypeValue) {
        return `content-type: ${contentTypeValue}\r\n`;
      }
    }
    return '';
  }

  setRequestHeader(header: string, value: string): void {
    this.customRequestHeaders.set(header, value);
  }

  abort(): void {
    const abort = makeProgressEvent(ABORT);
    if (this.kxhr.onabort) {
      this.kxhr.onabort(abort);
    }
    this.emitter.dispatchEvent(abort);
    this.cleanup();
  }

  intercept(responsePromise: Promise<HttpResponse>) {
    const complete = (response: HttpResponse) => {
      // notify start
      const start = retargetEvent(makeProgressEvent(LOAD_START), this.kxhr);
      if (this.kxhr.onloadstart) {
        this.kxhr.onloadstart(start as Event);
      }
      this.emitter.dispatchEvent(start);

      // notifyHeadersReceived
      this.kxhr.status = response.status;
      this.kxhr.statusText = response.statusText;
      this.kxhr.readyState = 2;
      const headersReceived = retargetEvent(
        makeProgressEvent(READY_STATE_CHANGE),
        this.kxhr
      );
      if (this.kxhr.onreadystatechange) {
        this.kxhr.onreadystatechange(headersReceived);
      }
      this.emitter.dispatchEvent(headersReceived);

      // notifyLoading
      if (typeof response.body !== 'undefined') {
        this.kxhr._setResponse(response.body);
      }
      this.kxhr.readyState = 3;
      const readyStateLoading = retargetEvent(
        makeProgressEvent(READY_STATE_CHANGE),
        this.kxhr
      );
      if (this.kxhr.onreadystatechange) {
        this.kxhr.onreadystatechange(readyStateLoading);
      }
      this.emitter.dispatchEvent(readyStateLoading);

      // emit progress
      const progress = retargetEvent(makeProgressEvent(PROGRESS), this.kxhr);
      if (this.kxhr.onreadystatechange) {
        this.kxhr.onreadystatechange(progress);
      }
      this.emitter.dispatchEvent(progress);

      // notifyDone
      this.kxhr.readyState = 4;
      const readyStateDone = retargetEvent(
        makeProgressEvent(READY_STATE_CHANGE),
        this.kxhr
      );
      if (this.kxhr.onreadystatechange) {
        this.kxhr.onreadystatechange(readyStateDone);
      }
      this.emitter.dispatchEvent(readyStateDone);

      // notifyLoaded
      const loaded = retargetEvent(makeEvent(LOAD), this.kxhr);
      if (this.kxhr.onload) {
        this.kxhr.onload(loaded);
      }
      this.emitter.dispatchEvent(loaded);

      // notifyLoadEnd
      const loadEnd = retargetEvent(makeEvent(LOAD_END), this.kxhr);
      if (this.kxhr.onloadend) {
        this.kxhr.onloadend(loadEnd);
      }
      this.emitter.dispatchEvent(loadEnd);

      this.cleanup();
    };

    responsePromise
      .then(response => {
        complete(response);
      })
      .catch(error => {
        log.warn(`failed to resolve request for ${this.url}`, error);
        const e = makeProgressEvent(ERROR);
        this.emitter.dispatchEvent(e);
        if (this.kxhr.onerror) {
          this.kxhr.onerror(e);
        }

        this.kxhr.readyState = 4;
        this.kxhr.status = error.status || 0;
        const readyStateDone = retargetEvent(
          makeProgressEvent(READY_STATE_CHANGE),
          this.kxhr
        );
        if (this.kxhr.onreadystatechange) {
          this.kxhr.onreadystatechange(readyStateDone);
        }
        this.emitter.dispatchEvent(readyStateDone);
        log.warn('Failed to serve request.', error, this.kxhr);
        this.cleanup();
        this.abort();
      });
  }

  addEventListener(name: string, handler: EventListener): void {
    this.emitter.addEventListener(name, handler);
  }

  dispatchEvent(event: Event): boolean {
    return this.emitter.dispatchEvent(event);
  }

  removeEventListener(type: string, callback: EventListener | null): void {
    if (callback) {
      this.emitter.removeEventListener(type, callback);
    }
  }

  cleanup() {
    this.emitter.cleanup();
  }
}

export interface KollectiveXHROptions {
  sourceType: SourceType;
  playType: PlayType;
  stack: InterceptorStack;
}

export default class KollectiveXHR implements EventTarget, State {
  private internal: InternalImpl;
  private opened = false;
  private sourceType: SourceType;
  private playType: PlayType;
  private stack: InterceptorStack;
  private headers: Record<string, string> = {};
  private mimeType?: string;
  private _responseText?: string;

  readyState: ReadyState;
  response?: HttpResponseBody;
  responseType: HttpResponseType;
  status?: number | undefined;
  statusText?: string | undefined;
  timeout = 0;

  withCredentials = false;
  onreadystatechange?: EventListener;
  onload?: EventListener;
  onloadend?: EventListener;
  onloadstart?: EventListener;
  onabort?: EventListener;
  onerror?: EventListener;

  constructor(options: KollectiveXHROptions) {
    autobind(this);
    const { sourceType, playType, stack } = options;
    this.sourceType = sourceType;
    this.playType = playType;
    this.stack = stack;
    this.readyState = 0;
    this.responseType = '';
    this.internal = new InternalImpl(
      this,
      this.stack,
      this.sourceType,
      this.playType
    );
  }

  _getCharset(): string | undefined {
    if (!isString(this.mimeType)) {
      return;
    }
    const cstr = 'charset=';
    const idx = this.mimeType.indexOf(cstr) + cstr.length;
    const charset = this.mimeType.substring(idx);
    if (charset.indexOf(';') > -1) {
      return charset.substring(0, charset.indexOf(';'));
    }
    return charset;
  }

  _setResponse(body: HttpResponseBody): void {
    const item = convertResponseByType(this.responseType, body);
    this._responseText = item.text;
    this.response = item.response;
  }

  get responseText(): string | undefined {
    if (this._responseText) {
      return this._responseText;
    }
    if (isArrayBuffer(this.response)) {
      const charset = this._getCharset();
      if (charset && charset.toLowerCase() === 'x-user-defined') {
        return decodeXuserDefined(this.response);
      } else if (charset) {
        return new TextDecoder(charset).decode(this.response);
      }
    }
    // NOTE: We only handle ArrayBuffers and strings currently.
    // The array will be decoded into utf-8. To decode other charsets
    // the mime type needs to be set using overrideMimeType.
    const { data } = maybeDecode(this.response);
    return isString(data) ? data : undefined;
  }

  // XMLHttpRequest.open()
  // Initializes a request.
  open(
    method: HttpMethod,
    url: string,
    // These three are here for api harmony, but are not used at the moment
    _async?: boolean, // always async
    _user?: string,
    _password?: string
  ): void {
    this.opened = true;
    this.internal.open(method, url);
  }

  // XMLHttpRequest.setRequestHeader()
  // Sets the value of an HTTP request header.
  setRequestHeader(header: string, value: string): void {
    if (this.opened) {
      this.internal.setRequestHeader(header, value);
    } else {
      this.headers[header] = value;
    }
  }

  // XMLHttpRequest.send()
  // Sends the request. If the request is asynchronous (which is the default), this method returns as soon as the request is sent.
  send(body?: HttpRequestBody): void {
    Object.entries(this.headers).forEach(([header, value]) => {
      this.internal.setRequestHeader(header, value);
    });
    this.headers = {};
    // TODO - handle the notion of reuse?
    if (!this.opened) {
      throw new DOMException(
        "Failed to execute 'send' on 'XMLHttpRequest': The object's state must be OPENED."
      );
    }
    this.internal.send(body);
    // clear the opened state
    this.opened = false;
  }

  // XMLHttpRequest.getResponseHeader()
  // Returns the string containing the text of the specified header, or null if either the response has not yet been received or the header doesn't exist in the response.
  getResponseHeader(headerName: string): string | null {
    return this.internal.getResponseHeader(headerName);
  }

  // XMLHttpRequest.getAllResponseHeaders()
  // Returns all the response headers, separated by CRLF, as a string, or null if no response has been received.
  getAllResponseHeaders(): string | null {
    return this.internal.getAllResponseHeaders();
  }

  // XMLHttpRequest.abort()
  // Aborts the request if it has already been sent.
  abort(): void {
    this.internal.abort();
  }

  // XMLHttpRequest.overrideMimeType()
  // Overrides the MIME type returned by the server.
  overrideMimeType(mimeType: string): void {
    this.mimeType = mimeType;
  }

  addEventListener(eventName: string, listener: EventListener): void {
    this.internal.addEventListener(eventName, listener);
  }

  dispatchEvent(event: Event): boolean {
    return this.internal.dispatchEvent(event);
  }

  removeEventListener(type: string, callback: EventListener | null): void {
    this.internal.removeEventListener(type, callback);
  }

  // SRV-878 - AMP is serializing this object for some reason.
  // So to fix we implement toJson as defined here - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#toJSON_behavior
  toJSON(): unknown {
    // There is nothing to see here.
    const { readyState, response, responseText, status, statusText } = this;
    return { readyState, response, responseText, status, statusText };
  }
}
